/*
 * <lh-show-more> — reveal/collapse a list past an initial count.
 *
 *   <lh-show-more data-initial="6" data-items="[data-show-more-item]">
 *     … items, each carrying the item selector …
 *     <button type="button" data-show-more-toggle
 *             data-more-label="Load more" data-less-label="Show less">
 *       <span data-show-more-label>Load more</span>
 *     </button>
 *   </lh-show-more>
 *
 * Hides everything past data-initial, then toggles. Purely client side —
 * every item is already in the DOM.
 */

(() => {

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

class ShowMore extends HTMLElement {
  #items = [];
  #toggle = null;
  #label = null;

  connectedCallback() {
    this.#toggle = this.querySelector('[data-show-more-toggle]');
    if (!this.#toggle) return;

    const selector = this.getAttribute('data-items') || '[data-show-more-item]';
    this.#items = Array.from(this.querySelectorAll(selector));
    this.#label = this.#toggle.querySelector('[data-show-more-label]');

    const initial = parseInt(this.getAttribute('data-initial'), 10) || 0;

    // Nothing to reveal — the control would be a dead end, so drop it.
    if (!initial || this.#items.length <= initial) {
      this.#toggle.hidden = true;
      return;
    }

    this.#toggle.hidden = false;
    this.#toggle.addEventListener('click', this.#onToggle);
    this.#collapse(false);
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#onToggle);
  }

  get expanded() {
    return this.#toggle?.getAttribute('aria-expanded') === 'true';
  }

  #onToggle = () => {
    if (this.expanded) {
      this.#collapse(true);
    } else {
      this.#expand();
    }
  };

  #initial() {
    return parseInt(this.getAttribute('data-initial'), 10) || 0;
  }

  #expand() {
    const initial = this.#initial();
    const revealed = this.#items.slice(initial);
    const animate = !window.matchMedia(REDUCED_MOTION).matches;

    revealed.forEach((item, i) => {
      item.hidden = false;
      if (!animate) return;
      // Cap the stagger — past the eighth item the delay reads as a stall.
      item.style.setProperty('--lh-show-more-index', String(Math.min(i, 8)));
      item.setAttribute('data-show-more-entering', '');
      item.addEventListener(
        'animationend',
        () => {
          item.removeAttribute('data-show-more-entering');
          item.style.removeProperty('--lh-show-more-index');
        },
        { once: true }
      );
    });

    this.#setState(true);
  }

  #collapse(focusBack) {
    const initial = this.#initial();
    this.#items.forEach((item, i) => {
      item.hidden = i >= initial;
      item.removeAttribute('data-show-more-entering');
    });

    this.#setState(false);

    if (!focusBack) return;

    // Collapsing can leave the toggle above the fold; bring it back into view.
    const top = this.getBoundingClientRect().top;
    if (top < 0) {
      this.scrollIntoView({
        block: 'start',
        behavior: window.matchMedia(REDUCED_MOTION).matches ? 'auto' : 'smooth',
      });
    }
    this.#toggle.focus();
  }

  #setState(expanded) {
    this.#toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.toggleAttribute('data-expanded', expanded);

    const next = expanded
      ? this.#toggle.dataset.lessLabel
      : this.#toggle.dataset.moreLabel;

    if (next && this.#label) this.#label.textContent = next;
  }
}

if (!customElements.get('lh-show-more')) {
  customElements.define('lh-show-more', ShowMore);
}

})();
